// src/hooks/useRestriccionMorosidad.js
import { useEffect, useState } from 'react';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { calcularEstadoMoroso, esCasaMorosa } from '../utils/morosoUtils';

/**
 * Hook para verificar si un residente tiene restricciones por morosidad
 * @returns {Object} { isRestricted, restrictedSections, loading, userDebt }
 */
export const useRestriccionMorosidad = () => {
    const { userData } = useAuth();
    const [config, setConfig] = useState(null);
    const [userDebt, setUserDebt] = useState({ adeudo: 0, moroso: false });
    const [loading, setLoading] = useState(true);

    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd;
    const role = String(userData?.rol || '').toLowerCase();

    // Intentar obtener casaId de múltiples fuentes posibles
    const casaId = userData?.casaId || userData?.casaID || userData?.idCasa || null;
    const numeroCasa = userData?.numero || userData?.numeroCasa || null;

    // Solo aplicar restricciones a residentes (rol 'user')
    const isResident = role === 'user' || role === 'residente';

    console.log('🔍 useRestriccionMorosidad - Inicialización:', {
        fraccionamientoId,
        casaId,
        numeroCasa,
        role,
        isResident,
        userData: userData ? { ...userData } : null
    });

    useEffect(() => {
        let isMounted = true;
        if (!fraccionamientoId || !isResident) {
            setLoading(false);
            return () => { isMounted = false; };
        }

        // Escuchar configuración financiera del fraccionamiento
        const unsubConfig = onSnapshot(
            doc(db, 'fraccionamientos', fraccionamientoId),
            (snap) => {
                if (!isMounted) return;
                if (snap.exists()) {
                    const data = snap.data();
                    setConfig(data.configuracionFinanciera || null);
                } else {
                    setConfig(null);
                }
            },
            (error) => {
                if (isMounted) {
                    console.error('useRestriccionMorosidad: Error al cargar configuración', error);
                    setConfig(null);
                }
            }
        );

        const cleanups = [unsubConfig];

        // Escuchar el adeudo del usuario (su casa)
        if (casaId) {
            console.log('📋 Escuchando casa por ID:', casaId);
            const unsubCasa = onSnapshot(
                doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId),
                (snap) => {
                    if (!isMounted) return;
                    if (snap.exists()) {
                        const data = snap.data();
                        const adeudo = Number(data.adeudo || 0);
                        const cuotaMensual = Number(config?.montoMensual || config?.cuotaMensual || 0);
                        const mesesPermitidos = Number(config?.interesesMesesPermitidosDeuda ?? 1);
                        const moroso = esCasaMorosa(data, cuotaMensual, mesesPermitidos);
                        setUserDebt({ adeudo, moroso, casaData: { id: snap.id, ...data } });
                    } else {
                        setUserDebt({ adeudo: 0, moroso: false, casaData: null });
                    }
                    setLoading(false);
                },
                (error) => {
                    if (isMounted) {
                        console.error('❌ Error al cargar deuda usuario:', error);
                        setUserDebt({ adeudo: 0, moroso: false, casaData: null });
                        setLoading(false);
                    }
                }
            );
            cleanups.push(unsubCasa);
        } else if (numeroCasa) {
            console.log('📋 Buscando casa por número:', numeroCasa);
            const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
            const q = query(casasRef, where('numero', '==', numeroCasa));

            const unsubCasa = onSnapshot(q, (snapshot) => {
                if (!isMounted) return;
                if (!snapshot.empty) {
                    const docSnap = snapshot.docs[0];
                    const data = docSnap.data();
                    const adeudo = Number(data.adeudo || 0);
                    const cuotaMensual = Number(config?.montoMensual || config?.cuotaMensual || 0);
                    const mesesPermitidos = Number(config?.interesesMesesPermitidosDeuda ?? 1);
                    const moroso = esCasaMorosa(data, cuotaMensual, mesesPermitidos);
                    setUserDebt({ adeudo, moroso, casaData: { id: docSnap.id, ...data } });
                } else {
                    setUserDebt({ adeudo: 0, moroso: false, casaData: null });
                }
                setLoading(false);
            }, (error) => {
                if (isMounted) {
                    console.error('❌ Error al buscar casa por número:', error);
                    setUserDebt({ adeudo: 0, moroso: false, casaData: null });
                    setLoading(false);
                }
            });
            cleanups.push(unsubCasa);
        } else {
            setLoading(false);
        }

        return () => {
            isMounted = false;
            cleanups.forEach(unsub => unsub());
        };
    }, [fraccionamientoId, isResident, casaId, numeroCasa, config?.montoMensual, config?.cuotaMensual, config?.interesesMesesPermitidosDeuda]);

    // Calcular si hay restricciones activas
    const isRestricted = () => {
        // Debug logs
        console.log('🔍 useRestriccionMorosidad - Verificando restricción:', {
            isResident,
            fraccionamientoId,
            casaId,
            numeroCasa,
            configEnabled: config?.restriccionServiciosEnabled,
            userDebt,
            config: config ? {
                enabled: config.restriccionServiciosEnabled,
                servicios: config.restriccionServicios,
                meses: config.restriccionMeses,
                montoMensual: config.montoMensual
            } : null
        });

        // Si no es residente, no aplicar restricciones
        if (!isResident) {
            console.log('❌ No es residente');
            return false;
        }

        // Si no hay configuración o no está habilitada, no hay restricción
        if (!config || !config.restriccionServiciosEnabled) {
            console.log('❌ Configuración no existe o no está habilitada');
            return false;
        }

        // REGLA PRINCIPAL: adeudo > 0 = MOROSO
        // Si el usuario no tiene adeudo, no hay restricción
        if (!userDebt.moroso || userDebt.adeudo <= 0) {
            console.log('❌ Usuario no tiene adeudo');
            return false;
        }

        // LÓGICA DE MESES DE RESTRICCIÓN:
        // restriccionMeses = 0: Se bloquea inmediatamente si tiene adeudo >= 1 (o adeudo > 0)
        // restriccionMeses = 1: Se bloquea cuando debe 1+ mes
        // restriccionMeses = 2+: Se bloquea cuando debe 2+ meses
        const mesesRequeridos = Number(config.restriccionMeses !== undefined && config.restriccionMeses !== null && config.restriccionMeses !== '' ? config.restriccionMeses : 0);
        const cuotaMensual = Number(config.montoMensual || 0);

        console.log('📊 Calculando meses de adeudo:', {
            adeudo: userDebt.adeudo,
            cuotaMensual,
            mesesRequeridos
        });

        // Si meses requeridos es 0, cualquier adeudo >= 1 activa la restricción
        if (mesesRequeridos === 0) {
            const aplica = userDebt.adeudo >= 1;
            console.log(`📊 Restricción tolerancia 0: adeudo ${userDebt.adeudo} >= 1 -> ${aplica}`);
            return aplica;
        }

        // Si no hay cuota mensual configurada, aplicar restricción inmediatamente si adeudo > 0
        if (cuotaMensual <= 0) {
            console.log('⚠️ No hay cuota mensual configurada. Aplicando restricción por adeudo > 0');
            return true;
        }

        // Calcular cuántos meses de deuda tiene
        const mesesAdeudo = Math.floor(userDebt.adeudo / cuotaMensual);

        console.log(`📊 Usuario debe ${mesesAdeudo} meses (requiere ${mesesRequeridos} para bloqueo)`);

        // Aplicar bloqueo si cumple o supera los meses requeridos
        if (mesesAdeudo >= mesesRequeridos) {
            console.log(`✅ RESTRICCIÓN ACTIVA - Debe ${mesesAdeudo} meses (>= ${mesesRequeridos} requeridos)`);
            return true;
        } else {
            console.log(`❌ Sin restricción - Debe ${mesesAdeudo} meses (< ${mesesRequeridos} requeridos)`);
            return false;
        }
    };

    const hasActiveRestrictions = isRestricted();
    const detalleConfig = config?.restriccionesDetalle || null;

    // Verificar si una pestaña completa está restringida
    const isTabRestricted = (tabKey) => {
        if (!hasActiveRestrictions) return false;
        if (detalleConfig && detalleConfig[tabKey]) {
            return Boolean(detalleConfig[tabKey].bloqueoTotal);
        }
        // Fallback a array legacy si no existe detalle granular
        const legacy = Array.isArray(config?.restriccionServicios) ? config.restriccionServicios : [];
        if (tabKey === 'amenidades') return legacy.includes('amenidades');
        if (tabKey === 'vigilancia') return legacy.includes('visitas');
        return false;
    };

    // Verificar si una subsección específica está restringida
    const isSubSectionRestricted = (tabKey, subKey) => {
        if (!hasActiveRestrictions) return false;
        // Si la pestaña principal está bloqueada, bloquea todas sus subsecciones
        if (isTabRestricted(tabKey)) return true;

        if (detalleConfig && detalleConfig[tabKey]?.subsecciones) {
            return Boolean(detalleConfig[tabKey].subsecciones[subKey]);
        }

        // Mapeo de compatibilidad con array legacy
        const legacy = Array.isArray(config?.restriccionServicios) ? config.restriccionServicios : [];
        if (tabKey === 'comunidad') {
            if (subKey === 'chat_comunitario') return legacy.includes('chat');
            if (subKey === 'solicitudes_permisos') return legacy.includes('solicitudes');
            if (subKey === 'directorio_contactos') return legacy.includes('datosContacto');
        } else if (tabKey === 'amenidades') {
            if (subKey === 'reservar_amenidades') return legacy.includes('amenidades');
        } else if (tabKey === 'vigilancia') {
            if (subKey === 'generar_qr' || subKey === 'visitas_frecuentes') return legacy.includes('visitas');
        }

        return false;
    };

    // Verificar si se debe mostrar el aviso cordial en Finanzas
    const debeMostrarAvisoFinanzas = Boolean(
        hasActiveRestrictions &&
        (detalleConfig?.finanzas?.mostrarModalAviso !== undefined
            ? detalleConfig.finanzas.mostrarModalAviso
            : true)
    );

    // Obtener las secciones restringidas (retrocompatibilidad)
    const getRestrictedSections = () => {
        if (!hasActiveRestrictions) return [];
        return Array.isArray(config?.restriccionServicios) ? config.restriccionServicios : [];
    };

    // Verificar si una sección específica está restringida (por clave de ruta o componente)
    const isSectionRestricted = (sectionKey) => {
        if (!hasActiveRestrictions) return false;

        // Mapear claves conocidas
        if (sectionKey === 'chat') {
            return isSubSectionRestricted('comunidad', 'chat_comunitario');
        }
        if (sectionKey === 'solicitudes') {
            return isSubSectionRestricted('comunidad', 'solicitudes_permisos');
        }
        if (sectionKey === 'datosContacto') {
            return isSubSectionRestricted('comunidad', 'directorio_contactos');
        }
        if (sectionKey === 'amenidades') {
            return isTabRestricted('amenidades') || isSubSectionRestricted('amenidades', 'reservar_amenidades');
        }
        if (sectionKey === 'visitas') {
            return isTabRestricted('vigilancia') || isSubSectionRestricted('vigilancia', 'generar_qr');
        }

        const restricted = getRestrictedSections();
        return restricted.includes(sectionKey);
    };

    return {
        isRestricted: hasActiveRestrictions,
        restrictedSections: getRestrictedSections(),
        isSectionRestricted,
        isTabRestricted,
        isSubSectionRestricted,
        debeMostrarAvisoFinanzas,
        loading,
        userDebt,
        config,
    };
};
